import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { View, useWindowDimensions, ImageBackground } from "react-native";

import { useSafeAreaInsets } from "react-native-safe-area-context";
import ConfettiCannon from "react-native-confetti-cannon";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  Button,
  Heading,
  ButtonText,
  VStack,
  useToast,
  Toast,
  ToastDescription,
  ToastTitle,
} from "@gluestack-ui/themed";

import { countries } from "../../data/quiz";

const colors = ["#ffb1ea", "#ff7ed7", "#ff4cbf", "#ff1aa5"];

const pinks = [
  "#ffe1fd",
  "#ffb1ea",
  "#ff7ed7",
  "#ff4cbf",
  "#ff1aa5",
  "#e60083",
  "#b40072",
  "#82005b",
  "#50003d",
  "#20001a",
  "#FF0092",
];

const App = () => {
  const [correctPoints, setCorrectPoints] = useState(0);
  const [wrongPoints, setWrongPoints] = useState(0);
  const [quiz, setQuiz] = useState();
  const [loading, setLoading] = useState(true);

  const { top, bottom } = useSafeAreaInsets();

  const explosion = useRef();

  const handleSomeKindOfEvent = () => {
    explosion.current && explosion.current.start();
  };

  const newQuiz = () => {
    const quizData = getQuizData();
    setQuiz(quizData);
    setLoading(false);
  };

  useEffect(() => {
    newQuiz();
  }, []);

  const { width, height } = useWindowDimensions();

  return (
    <ImageBackground
      style={{ flex: 1 }}
      source={require("../../../assets/gradient.png")}
    >
      <View
        style={{
          flex: 1,
          overflow: "hidden",
        }}
      >
        <View style={{ height: top }} />
        <Header
          correctPoints={correctPoints}
          wrongPoints={wrongPoints}
          quiz={quiz}
        />

        {!loading && (
          <View style={{ flex: 1 }}>
            <View
              style={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Heading style={{ fontSize: 100 }}>{quiz.emoji}</Heading>
            </View>
            <View
              style={{
                gap: 12,
                padding: 24,
              }}
            >
              {quiz.answers.map((possibleAnswer, index) => (
                <Button
                  key={index}
                  onPress={() => {
                    const rightAnswer = possibleAnswer === quiz.answer;
                    if (rightAnswer) {
                      setCorrectPoints(correctPoints + 1);
                      handleSomeKindOfEvent();
                    } else {
                      setWrongPoints(wrongPoints + 1);
                    }
                    newQuiz();
                  }}
                  sx={{
                    p: "$6",
                    bg: "$white",
                    borderRadius: "$full",
                    ":hover": {
                      bg: pinks[0],
                      _text: {
                        color: pinks[4],
                      },
                    },
                  }}
                >
                  <ButtonText
                    color={pinks[10]}
                    fontSize={16}
                    fontWeight="600"
                    // style={{ letterSpacing: -0.75 }}
                  >
                    {possibleAnswer}
                  </ButtonText>
                </Button>
              ))}
            </View>
          </View>
        )}
        <ConfettiCannon
          colors={colors}
          count={50}
          origin={{ x: width / 2, y: height }}
          explosionSpeed={1000}
          fallSpeed={500}
          fadeOut
          ref={explosion}
          autoStart={false}
        />
      </View>
      <View
        style={{
          height: bottom,
        }}
      />
    </ImageBackground>
  );
};

const ScoreIndicator = ({ score, wrong }) => {
  return (
    <View
      style={{
        flexDirection: "row",
        gap: 4,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {wrong ? (
        <MaterialCommunityIcons
          name="close-box"
          size={32}
          color="white"
        />
      ) : (
        <MaterialCommunityIcons
          name="checkbox-marked"
          size={32}
          color="white"
        />
      )}
      <Heading color={"white"}>{score}</Heading>
    </View>
  );
};

ScoreIndicator.propTypes = {
  score: PropTypes.any,
  wrong: PropTypes.any,
};

const Header = ({ quiz, wrongPoints, correctPoints }) => {
  const toast = useToast();

  return (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        padding: 16,
        gap: 16,
      }}
    >
      <ScoreIndicator
        score={correctPoints}
        wrong={false}
      />
      <Button
        onPress={() => {
          toast.show({
            duration: 1500,
            placement: "top",
            render: ({ id }) => {
              return (
                <Toast
                  nativeId={id}
                  action="attention"
                  variant="solid"
                >
                  <VStack space="xs">
                    <ToastTitle>{quiz.country}</ToastTitle>
                    <ToastDescription>
                      {`Hint: ${quiz.country}'s capital city is the answer`}
                    </ToastDescription>
                  </VStack>
                </Toast>
              );
            },
          });
        }}
        sx={{
          flex: 1,
          padding: 0,
          backgroundColor: "transparent",
          borderColor: "white",
          borderWidth: 2,
          borderRadius: "$full",
          ":hover": {
            bg: "white",
            borderColor: "#ffe1fd",
            _text: {
              color: pinks[3],
            },
          },
          ":pressed": {
            bg: "$green600",
            _text: {
              color: "$white",
            },
          },
        }}
      >
        <ButtonText
          color={"white"}
          fontSize={14}
        >
          Hint
        </ButtonText>
      </Button>

      <ScoreIndicator
        score={wrongPoints}
        wrong={true}
      />
    </View>
  );
};

Header.propTypes = {
  correctPoints: PropTypes.any,
  quiz: PropTypes.shape({
    country: PropTypes.any,
  }),
  wrongPoints: PropTypes.any,
};

const extractData = (data) => {
  const capitalsArray = [];
  const emojisArray = [];
  const countriesArray = [];

  data.map((item) => {
    capitalsArray.push(item.capital);
    emojisArray.push(item.emoji);
    countriesArray.push(item.country);
  });

  return { capitalsArray, emojisArray, countriesArray };
};

const getQuizData = () => {
  const { capitalsArray, emojisArray, countriesArray } = extractData(countries);

  const index = Math.floor(Math.random() * countriesArray.length);
  const answer = capitalsArray[index];
  const emoji = emojisArray[index];
  const country = countriesArray[index];

  const tempArray = capitalsArray.filter((capital) => capital !== answer);

  const shuffledArray = tempArray.sort(() => 0.5 - Math.random());

  let answers = shuffledArray.slice(0, 3);

  const insertAt = Math.floor(Math.random() * (answers.length + 1));
  answers.splice(insertAt, 0, answer);

  return {
    answer,
    emoji,
    country,
    answers,
  };
};

export default App;
